import { useMemo, useState } from "react";
import { Card, Col, Container, Row, Table, Badge, Button as RBButton} from "react-bootstrap";
import { ColDef, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { useLoaderData, useNavigate } from "react-router-dom";
import { PlanDetails, PlanPriceDetails, SubscriberInfo } from "./Data/ApiTypes";
import { cents, showMoney, showMoneyShort, toCents } from "./Data/Money";
import { HeaderText } from "./Controls/HeaderText";
import { subtract } from "dinero.js";
import { Pencil, QrCode, X } from "react-bootstrap-icons";
import { colText, colNumber, DataGrid } from "./Controls/DataGrid";
import { useParamIds } from "./Data/Common";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { Button } from "./Controls/Button";
import { Api } from "./Data/Api";
import { PlanPriceAddModal } from "./FacilityPlanPriceAddModal";
import { PlanPriceAddReq } from "./Data/ApiTypes";
import { createInstant } from "./Data/ApiConverters";

export function FacilityPlanDetailsPage() {
    const nav = useNavigate();
    const { facilityId, planId } = useParamIds();
    const planData = useLoaderData() as PlanDetails;
    const [plan, setPlan] = useState<PlanDetails>(planData);
    const [showAddPrice, setShowAddPrice] = useState(false);
    const [selPrice, setSelPrice] = useState<PlanPriceDetails>( plan.prices[0] );

    function totalSubscribersImpl( subInfo: SubscriberInfo[] ) {
        return subInfo.map( x => x.quantity ).reduce( ( a, b ) => a + b, 0 );
    }

    function totalMonthlyImpl( p: PlanPriceDetails, numSpaces: number ): any {
        return cents( toCents( p.price ) * numSpaces );
    }

    const columns = useMemo<ColDef<SubscriberInfo>[]>( () => [
        colText(   'name'         ),
        colText(   'emailAddress' ),
        colText(   'phoneNumber'  ),
        {
            headerName: "Quantity",
            valueFormatter: (v: ValueFormatterParams<SubscriberInfo>) =>
            v.data?.quantity != null ? v.data.quantity.toString() : "0", 
        },
        { headerName: "Monthly MRR",
            valueFormatter: (v: ValueFormatterParams<SubscriberInfo>) => showMoney( totalMonthlyImpl( selPrice, v.data?.quantity! ) ),
        }
    ], [] );

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
    };

    const sortedPrices = plan.prices.toSorted( ( a, b ) => toCents( subtract( a.price, b.price ) ) );

    function handleAddPrice(req: PlanPriceAddReq) {
        Api.facilityPlanPriceAdd(facilityId, planId, req).then(
            resp => {
                if (resp.isOk) {
                    // Create a new PlanPriceDetails object from the response
                    const newPrice: PlanPriceDetails = {
                        planPriceId: resp.value.planPriceId,
                        planId: planId,
                        name: resp.value.name,
                        price: cents(resp.value.price),
                        createdByStaffId: 0, // This will be set by the server
                        createdWhen: createInstant(new Date().toISOString()),
                        subscribers: [],
                        couponCode: resp.value.couponCode
                    };

                    // Update the plan state with the new price
                    setPlan(prevPlan => ({
                        ...prevPlan,
                        prices: [...prevPlan.prices, newPrice]
                    }));
                    // Set the new price as selected
                    setSelPrice(newPrice);
                }
            }
        );
    }

    return (
        <Container fluid className="pt-2 h-100" >
            <PlanPriceAddModal
                show={showAddPrice}
                setShow={setShowAddPrice}
                onSave={handleAddPrice}
                prices={plan.prices}
            />
           
            <Row className="h-100">
                <Col className="h-100" style={{ display: "grid", gridTemplateRows: "auto 1fr 5px", gridAutoColumns: "1fr" }}>
                    <div className="d-flex align-items-center gap-2">
                        <RBButton variant="link" style={{marginRight:"-10px"}} onClick={() => nav(-1)}>← Back</RBButton>
                        <PageBar title={plan.name}>
                            {plan.isPrivate && (
                                <Badge className="fs-6">Private Plan</Badge>
                            )}
                            <ToolButton onClick={ () => nav( `/facility/${facilityId}/plan/${planId}/edit` ) }>
                                <Pencil color="maroon" /> Edit Plan
                            </ToolButton>
                            {!plan.isPrivate && (
                            <ToolButton onClick={ () => nav( `/facility/${facilityId}/plan/${planId}/sign` ) }>
                                <QrCode color="maroon" /> View QR Code
                            </ToolButton>
                             )}
                        </PageBar>
                    </div>
                    <Container fluid className="g-0 py-0 my-0 h-100">
                        <Row className="h-100">
                            <Col xs="4">
                                <Card className="p-0 m-0 gap-0">
                                    <Card.Header>
                                        <HeaderText className="text-center">
                                            Prices
                                        </HeaderText>
                                    </Card.Header>
                                    <Card.Body className="p-0 m-0 gap-0">
                                    <Table variant="hover">
                                    <thead>
                                        <tr>
                                        <th className="text-center">Price</th>
                                        <th className="text-center">Parkers</th>
                                        <th className="text-center">MRR</th>
                                        <th className="text-center">Coupon Code</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedPrices.map((p) => {
                                        const sel = p === selPrice ? "bg-primary text-white" : "";
                                        const monthly = totalMonthlyImpl(p, totalSubscribersImpl(p.subscribers));

                                        return (
                                            <tr
                                            key={p.planPriceId}
                                            className={`text-center ${sel}`}
                                            aria-selected={p === selPrice}
                                            onClick={() => setSelPrice(p)}
                                            style={{ cursor: "pointer" }}
                                            >
                                            <td className={`${sel} text-end pe-3`}>{showMoney(p.price)}</td>
                                            <td className={`${sel} text-center`}>
                                                {totalSubscribersImpl(p.subscribers)}
                                            </td>
                                            <td className={`${sel} text-end pe-3`}>
                                                {showMoneyShort(monthly)}
                                            </td>
                                            <td
                                                className={`${sel} text-center`}
                                                onClick={(e) => {
                                                e.stopPropagation(); // prevent row click
                                                const range = document.createRange();
                                                range.selectNodeContents(e.currentTarget);
                                                const selObj = window.getSelection();
                                                selObj!.removeAllRanges();
                                                selObj!.addRange(range);
                                                }}
                                                style={{ userSelect: "all" }}
                                            >
                                                {p.couponCode}
                                            </td>
                                            </tr>
                                        );
                                        })}
                                    </tbody>
                                    </Table>
                                    </Card.Body>
                                    <Card.Footer className="p-2">
                                        <Button className="w-100" onClick={() => setShowAddPrice(true)}>
                                            Add Price
                                        </Button>
                                    </Card.Footer>
                                </Card>
                            </Col>
                            <Col xs="8" className="h-100">
                                <div style={{ height: "100%", width: '100%' }}>
                                    <DataGrid
                                        rowData={selPrice.subscribers}
                                        columnDefs={columns}
                                        onGridReady={onGridReady}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </Col>
            </Row>
        </Container>
    );
}

